import { Component } from "../lib/component.ts";
import { parseLineItemProperties } from "../lib/line-item-properties.ts";

interface ModalPanelLike extends HTMLElement {
  open: () => void;
  close: () => void;
}

interface ProductPersonalizationLike extends HTMLElement {
  setEditContext: (context: LineItemContext) => void;
}

interface LineItemContext {
  key: string;
  quantity: number;
  properties: Record<string, string>;
  feeLineKey?: string;
}

function isModalPanelLike(el: Element | null): el is ModalPanelLike {
  if (!(el instanceof HTMLElement)) return false;
  const open: unknown = Reflect.get(el, "open");
  const close: unknown = Reflect.get(el, "close");
  return typeof open === "function" && typeof close === "function";
}

function isProductPersonalizationLike(el: Element | null): el is ProductPersonalizationLike {
  if (!(el instanceof HTMLElement)) return false;
  const fn: unknown = Reflect.get(el, "setEditContext");
  return typeof fn === "function";
}

class CartPersonalization extends Component {
  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("click", this.#handleClick, {
      signal: this.signal,
    });
  }

  #handleClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-action="edit-personalization"]');
    if (!(button instanceof HTMLButtonElement)) return;

    void this.#openEditor(button);
  };

  async #openEditor(button: HTMLButtonElement): Promise<void> {
    const handle = button.dataset.productHandle;
    if (!handle) return;

    const context = this.#parseLineItemContext(button);
    if (!context) return;

    button.disabled = true;

    try {
      const variantId = button.dataset.variantId;
      const url = `/products/${encodeURIComponent(handle)}?section_id=personalization-editor${variantId ? `&variant=${variantId}` : ""}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.statusText);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const modalEl = doc.getElementById("personalization-panel");

      if (!modalEl) return;

      const imported = document.importNode(modalEl, true);
      if (!(imported instanceof HTMLElement)) return;
      imported.id = "cart-personalization-panel";
      this.appendChild(imported);

      await customElements.whenDefined("modal-panel");
      if (!isModalPanelLike(imported)) return;
      const modal = imported;
      modal.open();

      await customElements.whenDefined("product-personalization");
      const editorEl = modal.querySelector("product-personalization");
      if (isProductPersonalizationLike(editorEl)) {
        editorEl.setEditContext(context);
      }

      modal.addEventListener(
        "close",
        () => {
          modal.remove();
        },
        { once: true },
      );
    } finally {
      button.disabled = false;
    }
  }

  #parseLineItemContext(button: HTMLButtonElement): LineItemContext | null {
    const key = button.dataset.lineItemKey ?? "";
    const quantity = Number(button.dataset.quantity);
    if (!key || !(quantity > 0)) return null;

    const row = button.closest("[data-properties]");

    let rawProps: unknown;
    try {
      rawProps = JSON.parse(row?.getAttribute("data-properties") ?? "[]");
    } catch {
      return null;
    }

    const properties = parseLineItemProperties(rawProps);
    if (!properties) return null;

    const feeLineKey = row?.getAttribute("data-fee-line-key") ?? undefined;

    return { key, quantity, properties, feeLineKey };
  }
}

if (!customElements.get("cart-personalization")) {
  customElements.define("cart-personalization", CartPersonalization);
}
